import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Avatar,
  Paper,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  LocalDrink,
  ScreenShare,
  CheckCircle,
  Cancel,
  Schedule,
  Star,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useSnackbar } from 'notistack';
import SiteWrapper from '../../home-pages/SiteWrapper';
import { useFirebaseAuthentication } from 'lib/firebase';
import { getUserPurchases, PURCHASE_STATUS } from 'lib/purchaseUtils';

const PurchaseHistory = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const user = useFirebaseAuthentication();
  
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPurchases = async () => {
      if (user) {
        try {
          const userPurchases = await getUserPurchases(user.uid);
          setPurchases(userPurchases);
        } catch (error) {
          enqueueSnackbar('Error loading your purchase history', { variant: 'error' });
        } finally {
          setLoading(false);
        }
      }
    };

    loadPurchases();
  }, [user, enqueueSnackbar]);

  const getStatusInfo = (status) => {
    switch (status) {
      case PURCHASE_STATUS.PENDING:
        return {
          color: '#ff9800',
          icon: Schedule,
          label: 'Pending Approval',
          bgColor: '#fff3e0',
        };
      case PURCHASE_STATUS.APPROVED:
        return {
          color: '#4caf50',
          icon: CheckCircle,
          label: 'Approved',
          bgColor: '#e8f5e8',
        };
      case PURCHASE_STATUS.DENIED:
        return {
          color: '#f44336',
          icon: Cancel,
          label: 'Denied',
          bgColor: '#ffebee',
        };
      default:
        return {
          color: '#9e9e9e',
          icon: Schedule,
          label: 'Unknown',
          bgColor: '#f5f5f5',
        };
    }
  };

  const getItemIcon = (itemName) => {
    if (itemName.toLowerCase().includes('slushie')) return LocalDrink;
    if (itemName.toLowerCase().includes('screen')) return ScreenShare;
    return Star;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    
    // Handle Firestore timestamp
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <SiteWrapper>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress size={60} />
        </Box>
      </SiteWrapper>
    );
  }

  return (
    <SiteWrapper>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          py: 4,
        }}
      >
        <Container maxWidth="lg">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Paper
              elevation={8}
              sx={{
                p: 3,
                mb: 4,
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                borderRadius: 4,
                textAlign: 'center',
              }}
            >
              <Typography
                variant={isMobile ? 'h4' : 'h3'}
                fontWeight="bold"
                gutterBottom
                sx={{ textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}
              >
                📋 Purchase History
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                Track all your store purchases and their status
              </Typography>
            </Paper>
          </motion.div>

          {/* Purchase List */}
          {purchases.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Paper
                elevation={4}
                sx={{
                  p: 6,
                  textAlign: 'center',
                  borderRadius: 4,
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                }}
              >
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  No purchases yet!
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Visit the store to make your first purchase.
                </Typography>
              </Paper>
            </motion.div>
          ) : (
            <Grid container spacing={3}>
              {purchases.map((purchase, index) => {
                const statusInfo = getStatusInfo(purchase.status);
                const IconComponent = getItemIcon(purchase.itemName);
                const StatusIcon = statusInfo.icon;

                return (
                  <Grid item xs={12} md={6} lg={4} key={purchase.id}>
                    <motion.div
                      initial={{ opacity: 0, y: 50 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <Card
                        sx={{
                          height: '100%',
                          borderRadius: 3,
                          overflow: 'hidden',
                          border: `2px solid ${statusInfo.color}`,
                          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                        }}
                      >
                        {/* Status Banner */}
                        <Box
                          sx={{
                            bgcolor: statusInfo.bgColor,
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Chip
                            icon={<StatusIcon />}
                            label={statusInfo.label}
                            sx={{
                              bgcolor: statusInfo.color,
                              color: 'white',
                              fontWeight: 'bold',
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(purchase.createdAt)}
                          </Typography>
                        </Box>

                        <CardContent sx={{ p: 3 }}>
                          {/* Item Info */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mb: 2,
                            }}
                          >
                            <Avatar
                              sx={{
                                bgcolor: `${statusInfo.color}20`,
                                color: statusInfo.color,
                                mr: 2,
                              }}
                            >
                              <IconComponent />
                            </Avatar>
                            <Box>
                              <Typography variant="h6" fontWeight="bold">
                                {purchase.itemName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {purchase.itemDescription}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Price */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 1,
                              bgcolor: '#f8f9fa',
                              borderRadius: 2,
                              mb: 2,
                            }}
                          >
                            <Star sx={{ color: '#ffd700', mr: 1 }} />
                            <Typography variant="h6" fontWeight="bold">
                              {purchase.itemPrice} Points
                            </Typography>
                          </Box>

                          {/* Status Message */}
                          {purchase.status === PURCHASE_STATUS.PENDING && (
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                              Waiting for parent approval...
                            </Typography>
                          )}
                          {purchase.status === PURCHASE_STATUS.APPROVED && (
                            <Typography variant="body2" color="success.main" textAlign="center">
                              ✅ Approved! Enjoy your reward!
                            </Typography>
                          )}
                          {purchase.status === PURCHASE_STATUS.DENIED && (
                            <Typography variant="body2" color="error.main" textAlign="center">
                              ❌ Not approved this time. Keep earning points!
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Container>
      </Box>
    </SiteWrapper>
  );
};

export default PurchaseHistory;